import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import OrderTransferModal from './OrderTransferModal';

import { orderApi } from 'api';

import {
  getOrderListTab,
  getOrderListColumns,
  ORDER_FILTER_OPTIONS,
} from 'constants/order';

const isOrderStatusUnfulfillableInUrl = searchString => {
  if (!searchString) return false;

  const urlParams = new URLSearchParams(searchString);
  const status = urlParams.get('order_status');

  return status === 'unfulfillable';
};

const Orders = () => {
  const location = useLocation();

  const tableRef = useRef();
  const debounceTimer = useRef(null);

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const initialFilter = { warehouse: selectedWarehouse?.id };

  const [isConnecting, setIsConnecting] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const handleOpenTransferModal = useCallback(orderId => {
    setSelectedOrderId(orderId);
    setTransferModalOpen(true);
  }, []);

  const handleCloseTransferModal = useCallback(() => {
    setTransferModalOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleTransferSuccess = useCallback(() => {
    tableRef.current?.refetch();
  }, []);

  const handleOrderConnect = useCallback(
    async orderId => {
      try {
        const response = await orderApi.connectOrders(orderId);

        if (response?.order_status === 'unfulfillable') {
          toast.error('Please check the product information');
        } else if (response?.order_status === 'processing') {
          toast.success('Order successfully connected');

          tableRef.current?.refetch();
        }
      } catch (error) {
        toast.error('Failed to connect order');
      }
    },
    [tableRef],
  );

  const handleManifestConnect = useCallback(async () => {
    // 버튼 상태 확인
    if (isConnecting) return;

    // 1. 비활성화 상태 설정 및 토스트 메시지 표시
    setIsConnecting(true);

    try {
      await orderApi.patchUnfulfillableOrderRelationConnect();

      tableRef.current?.refetch();
      alert(
        'It may take more than a few minutes. Please wait and Refresh the page after a few minutes.',
      );
    } catch (error) {
      // 실패 시 로딩 토스트 닫고 에러 메시지 표시
      toast.error('Failed to initiate manifest connection.');
    } finally {
      setIsConnecting(false);
    }
  }, [isConnecting]);

  useEffect(() => {
    return () => {
      setIsConnecting(false);
    };
  }, []);

  const currentSearchString = useMemo(() => {
    return location.search;
  }, [location.search]);

  // extendedColumns는 변경 없음
  const extendedColumns = useMemo(() => {
    const connectColumn = {
      id: 'connect',
      header: '',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, order_status } = original;

        const canConnect = order_status === 'unfulfillable';

        if (!canConnect) return null;

        return (
          <Button
            variant="success"
            size="sm"
            className="text-white"
            onClick={() => handleOrderConnect(id)}
          >
            Connect
          </Button>
        );
      },
    };

    const transferColumn = {
      id: 'transfer',
      header: '',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, order_status } = original;

        const canTransfer =
          order_status === 'unfulfillable' || order_status === 'processing';

        if (!canTransfer) return null;

        return (
          <Button
            variant="warning"
            size="sm"
            onClick={() => handleOpenTransferModal(id)}
          >
            Transfer
          </Button>
        );
      },
    };

    return [
      ...getOrderListColumns(tenant, warehouse),
      connectColumn,
      transferColumn,
    ];
  }, [tenant, warehouse, handleOrderConnect, handleOpenTransferModal]);

  const isUnfulfillable = isOrderStatusUnfulfillableInUrl(currentSearchString);

  const exportApis = useMemo(() => {
    const buttons = [
      {
        title: undefined, // 기본값 'Download List' 사용
        exportApi: params => orderApi.getOrderExport(params),
      },
    ];

    if (isUnfulfillable) {
      buttons.unshift({
        title: 'Download Unfulfilled SKUs',
        exportApi: params => orderApi.getUnfulfillableOrderExport(params),
      });
    }
    return buttons;
  }, [isUnfulfillable]);

  const headerActions = (
    <>
      {isUnfulfillable && (
        <Button
          variant="success"
          size="sm"
          onClick={handleManifestConnect}
          disabled={isConnecting}
        >
          {isConnecting ? 'Connecting...' : 'Connect All'}
        </Button>
      )}
    </>
  );

  return (
    <>
      <Table
        ref={tableRef}
        title="Orders"
        apiService={orderApi}
        sortable={true}
        columns={extendedColumns}
        filterOptions={ORDER_FILTER_OPTIONS}
        tabOptions={getOrderListTab(initialFilter)}
        initialFilter={initialFilter}
        headerActions={headerActions}
        exportApis={exportApis}
      />
      <OrderTransferModal
        show={transferModalOpen}
        onHide={handleCloseTransferModal}
        orderId={selectedOrderId}
        currentWarehouseId={selectedWarehouse?.id}
        onSuccess={handleTransferSuccess}
      />
    </>
  );
};

export default Orders;
